import React from "react";
import { SnackbarContext } from "../contexts/Snackbar";
import { snackbarSeverity } from "./useSnackbar";
import api from "../utils/api/authApi";
import {
  getViewableMimeType,
  saveBlob,
} from "../components/display/fileViewer/FileViewerUtils";

// Fetches a file as a blob and either opens it in FileViewerDialog or saves it.
// `path` is the full backend path (e.g. FILE_DOWNLOAD + fileId).
// `mimeType` overrides the extension-based lookup, for endpoints whose
// response type is known up front (e.g. the generated doctor's letter PDF).
export default function useFileViewer() {
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [viewerFile, setViewerFile] = React.useState(null);
  // Tracked per path so overlapping requests don't clear each other's loading state
  const [loadingPaths, setLoadingPaths] = React.useState([]);
  const viewerUrlRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (viewerUrlRef.current) {
        window.URL.revokeObjectURL(viewerUrlRef.current);
      }
    };
  }, []);

  function startLoading(path) {
    setLoadingPaths((prev) => [...prev, path]);
  }

  function stopLoading(path) {
    setLoadingPaths((prev) => {
      const index = prev.indexOf(path);
      return index === -1 ? prev : prev.filter((_, i) => i !== index);
    });
  }

  function isPathLoading(path) {
    return loadingPaths.includes(path);
  }

  async function fetchBlob(path) {
    const response = await api.get(path, { responseType: "blob" });
    return response.data;
  }

  async function downloadFile({ path, name }) {
    if (!path) return;
    try {
      startLoading(path);
      const blob = await fetchBlob(path);
      saveBlob(new Blob([blob]), name);
    } catch (e) {
      handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
    } finally {
      stopLoading(path);
    }
  }

  async function viewFile({ path, name, mimeType }) {
    if (!path) return;
    const type = mimeType ?? getViewableMimeType(name);
    // Can't be rendered by the browser (Word, Excel...), fall back to download
    if (!type) {
      downloadFile({ path, name });
      return;
    }
    try {
      startLoading(path);
      const blob = await fetchBlob(path);
      // Force the MIME type so the browser picks the right built-in renderer
      const typedBlob = new Blob([blob], { type });
      const url = window.URL.createObjectURL(typedBlob);
      if (viewerUrlRef.current) {
        window.URL.revokeObjectURL(viewerUrlRef.current);
      }
      viewerUrlRef.current = url;
      setViewerFile({ url, name, mimeType: type, blob: typedBlob });
    } catch (e) {
      handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
    } finally {
      stopLoading(path);
    }
  }

  // Downloads the file currently open in the viewer without fetching it again
  function downloadViewerFile() {
    if (viewerFile) {
      saveBlob(viewerFile.blob, viewerFile.name);
    }
  }

  function closeViewer() {
    if (viewerUrlRef.current) {
      window.URL.revokeObjectURL(viewerUrlRef.current);
      viewerUrlRef.current = null;
    }
    setViewerFile(null);
  }

  return {
    viewFile,
    downloadFile,
    downloadViewerFile,
    closeViewer,
    viewerFile,
    isPathLoading,
  };
}
